import { Emitter } from "@core/events/emitter";
import type { IProjectModel } from "@models/project/IProjectModel";
import type {
  ClickModifiers,
  ISelectionModel,
  RenameRequest,
  SelectionChange,
} from "./ISelectionModel";

/**
 * `SelectionModel` — in-memory implementation of {@link ISelectionModel}.
 *
 * Unity reference: `Models/SceneModel/SelectedGameObjectModel.cs`. The
 * web port keeps the id-set + primary + change-event surface; the rich
 * per-entity property/event surface (transform, visibility, animation,
 * merge/unmerge/…) stays absent until the M7 inspector work, where it
 * lives in per-context inspector models that read selection + write back
 * via `ProjectModel.applyEntitySnapshotChanges`.
 *
 * Subscribes to `IProjectModel.onActiveSceneChanged` so a scene switch
 * always clears selection (matches Unity behavior — selection is per
 * loaded scene, not global).
 */
export class SelectionModel implements ISelectionModel {
  readonly onChanged = new Emitter<SelectionChange>();
  readonly onRenameRequested = new Emitter<RenameRequest>();

  private m_selected = new Set<string>();
  private m_primaryId: string | null = null;
  private m_selectionSetId: string | null = null;
  /**
   * Latched by {@link setSelectionSet} for exactly one {@link commit} — the web
   * spelling of `EditorModel.m_selectionSetWasSelected` (`EditorModel.cs:344`),
   * which survives one selection change so the set is remembered and every
   * later change clears it.
   *
   * The id is held SEPARATELY from `m_selectionSetId` rather than written
   * straight onto it, so `commit` can still see the outgoing value and detect
   * a set-only change: two sets with identical membership produce no added or
   * removed ids, and without that comparison the second one would be swallowed.
   */
  private m_pendingSelectionSetId: string | null = null;
  private m_selectionSetPending = false;

  constructor(projectModel: IProjectModel) {
    projectModel.onActiveSceneChanged.on(() => this.clear());
    // Deliberately NOT also clearing on `onLoadSceneCompleted`. That event
    // used to be dead (`loadSceneAsync` was a stub); now that it fires for
    // real it lands AFTER the scene is on screen — up to a 4 s smooth
    // transition later — so clearing there would wipe a selection the user
    // just made. `onActiveSceneChanged` above is the Unity-equivalent moment
    // (`ProjectModelSlim.SetActiveScene` restores selection, it never clears
    // late).
  }

  get selectedIds(): ReadonlySet<string> {
    return this.m_selected;
  }

  get primaryId(): string | null {
    return this.m_primaryId;
  }

  get selectionSetId(): string | null {
    return this.m_selectionSetId;
  }

  isSelected(id: string): boolean {
    return this.m_selected.has(id);
  }

  click(id: string, modifiers: ClickModifiers = {}): void {
    const next = new Set(this.m_selected);
    if (modifiers.multiselect) {
      if (next.has(id)) next.delete(id);
      else next.add(id);
    } else if (modifiers.rangeselect) {
      next.add(id);
    } else {
      next.clear();
      next.add(id);
    }
    this.commit(next, id);
  }

  set(ids: Iterable<string>): void {
    const next = new Set(ids);
    const primary =
      this.m_primaryId && next.has(this.m_primaryId)
        ? this.m_primaryId
        : (next.values().next().value ?? null);
    this.commit(next, primary);
  }

  setSelectionSet(selectionSetId: string, memberIds: Iterable<string>): void {
    this.m_selectionSetPending = true;
    this.m_pendingSelectionSetId = selectionSetId;
    try {
      this.set(memberIds);
    } finally {
      this.m_selectionSetPending = false;
      this.m_pendingSelectionSetId = null;
    }
  }

  clear(): void {
    // No "already empty" guard: `commit` owns the unchanged test, and it also
    // has to fire when only the SELECTION SET changed (re-selecting a set with
    // the same members, or clearing a set whose members were already empty).
    this.commit(new Set(), null);
  }

  requestRename(snapshotId: string): void {
    this.onRenameRequested.emit({ snapshotId });
  }

  private commit(next: Set<string>, primary: string | null): void {
    const added: string[] = [];
    const removed: string[] = [];
    for (const id of next) if (!this.m_selected.has(id)) added.push(id);
    for (const id of this.m_selected) if (!next.has(id)) removed.push(id);

    // Unity clears `m_selectedSet` on any selection change that did not come
    // from `SelectSelectionSet`; the latch is what distinguishes the two.
    const nextSelectionSetId = this.m_selectionSetPending ? this.m_pendingSelectionSetId : null;
    const selectionSetChanged = nextSelectionSetId !== this.m_selectionSetId;

    if (
      added.length === 0
      && removed.length === 0
      && primary === this.m_primaryId
      && !selectionSetChanged
    ) {
      return;
    }
    this.m_selected = next;
    this.m_primaryId = primary;
    this.m_selectionSetId = nextSelectionSetId;
    this.onChanged.emit({
      selectedIds: this.m_selected,
      addedIds: added,
      removedIds: removed,
      primaryId: this.m_primaryId,
      selectionSetId: this.m_selectionSetId,
    });
  }
}
